import { apiFetch, API_BASE_URL } from "./apiFetch";
import { getToken } from "./sessionStorage";

export { API_BASE_URL };

// Shared query-param builder for the listing endpoints' optional filters
const buildListQueryParams = (page, limit, filters = {}) => {
  const { searchQuery, search, fulfillmentStatus, progressStatus, country, date, startDate, endDate } = filters;
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (searchQuery && searchQuery.trim()) params.set("searchQuery", searchQuery.trim());
  if (search && search.trim()) params.set("search", search.trim());
  if (fulfillmentStatus && fulfillmentStatus !== "all") params.set("fulfillmentStatus", fulfillmentStatus);
  if (progressStatus && progressStatus !== "all") params.set("progressStatus", progressStatus);
  if (country && country !== "all") params.set("country", country);
  if (date && date.trim()) params.set("date", date.trim());
  if (startDate && startDate.trim()) params.set("startDate", startDate.trim());
  if (endDate && endDate.trim()) params.set("endDate", endDate.trim());
  return params;
};

// Fetch orders using async/await with pagination
export const fetchOrders = (page = 1, limit = 130, filters = {}) =>
  apiFetch(`/orders/summary?${buildListQueryParams(page, limit, filters)}`);

// Fetch the deduped, sorted list of countries used across orders — powers country filter dropdowns
export const fetchOrderCountries = () => apiFetch("/orders/countries");

// Patient portal: summary cards + last 5 status changes across the patient's tests
export const fetchPatientDashboard = () => apiFetch("/patient/dashboard");

// Patient portal: all orders/tests, each with its full 7-step status timeline.
// progressStatus filters at the order level (order matches if any of its
// tests has that current_step_key) — omit/"all" for no filter.
export const fetchPatientOrders = (page = 1, limit = 20, progressStatus = "all") =>
  apiFetch(`/patient/orders?${buildListQueryParams(page, limit, { progressStatus })}`);

// Patient portal: the logged-in patient's own lab test reports (metadata/status only).
// filters: { search, date, startDate, endDate } — all optional, always combinable
// except date vs startDate/endDate (date wins if both are sent). `search` is one
// free-text box matched as a substring against order_number, test_identifier, OR
// substrate. Always sorted newest-first by completed_at server-side; no sort param.
export const fetchPatientReports = (page = 1, limit = 20, filters = {}) =>
  apiFetch(`/patient/reports?${buildListQueryParams(page, limit, filters)}`);

// Patient portal: fresh 5-minute presigned S3 view/download URL for one report — call on demand, never cache
export const fetchPatientReportLink = (labTestId) => apiFetch(`/patient/reports/${labTestId}/link`);

// Therapist portal: summary counts across all of this therapist's patients/tests
export const fetchTherapistDashboard = () => apiFetch("/therapist/dashboard");

// Therapist portal: patients grouped by (first_name, last_name, date_of_birth) — one row per patient, not per order.
// search matches on name (first/last), per the backend's ?search= param.
export const fetchTherapistPatients = (page = 1, limit = 20, search = "") =>
  apiFetch(`/therapist/patients?${buildListQueryParams(page, limit, { search })}`);

// Therapist portal: one patient's orders/tests (same shape/filter semantics as fetchPatientOrders), scoped to this therapist
export const fetchTherapistPatientOrders = (patientKey, page = 1, limit = 20, progressStatus = "all") =>
  apiFetch(`/therapist/patients/${patientKey}/orders?${buildListQueryParams(page, limit, { progressStatus })}`);

// Therapist portal: reports across every patient linked to this therapist.
// Same optional filters as fetchPatientReports: { search, date, startDate, endDate }.
export const fetchTherapistReports = (page = 1, limit = 20, filters = {}) =>
  apiFetch(`/therapist/reports?${buildListQueryParams(page, limit, filters)}`);

// Therapist portal: fresh 5-minute presigned S3 view/download URL for one report — call on demand, never cache
export const fetchTherapistReportLink = (labTestId) => apiFetch(`/therapist/reports/${labTestId}/link`);

// Fetch order details for processing
export const fetchOrderDetails = (orderIds) =>
  apiFetch("/orders/orderDetails", { method: "POST", body: JSON.stringify({ orderIds }) });

// Register a new therapist
// Expected JSON format: { therapist_name, practice_name, city, therapist_email }
export const registerTherapist = (therapistData) =>
  apiFetch("/manageTherapists/registerTherapists", {
    method: "POST",
    body: JSON.stringify(therapistData),
  });

// Delete a therapist
export const deleteTherapist = (therapistId) =>
  apiFetch(`/manageTherapists/deleteTherapists/${therapistId}`, { method: "DELETE" });

// Update a therapist
// Expected JSON format: { therapist_name, practice_name, city, therapist_email }
export const updateTherapist = (therapistId, therapistData) =>
  apiFetch(`/manageTherapists/updateTherapists/${therapistId}`, {
    method: "PUT",
    body: JSON.stringify(therapistData),
  });

// Fetch therapists list (Admin/Authenticated users)
export const fetchTherapists = (page = 1, limit = 130) =>
  apiFetch(`/manageTherapists/getTherapists?page=${page}&limit=${limit}`);

// Fetch therapists list for client/public use (no authentication required)
export const fetchTherapistForClient = async (page = 1, limit = 130) => {
  const response = await fetch(
    `${API_BASE_URL}/manageTherapists/getTherapistsForClient?page=${page}&limit=${limit}`,
    { headers: { Accept: "application/json", "ngrok-skip-browser-warning": "true" } }
  );
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  return response.json();
};

// Add therapist for patient form (when "other therapist" is selected) — public
export const addTherapistForClient = async (therapistData) => {
  const response = await fetch(`${API_BASE_URL}/manageTherapists/addTherapistsForClient`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
    body: JSON.stringify(therapistData),
  });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  return response.json();
};

// Submit SIBO form data — public
export const submitSiboForm = async (formData) => {
  const response = await fetch(`${API_BASE_URL}/managePatientForms/submitForm`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
    body: JSON.stringify(formData),
  });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  return response.json();
};

// Fetch form data by lab test ID — public, but attaches a token if one is present
export const fetchFormByLabTestId = async (labTestId) => {
  const headers = { Accept: "application/json", "ngrok-skip-browser-warning": "true" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}/managePatientForms/form/${labTestId}`, { headers });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  return response.json();
};

// Record an actual barcode scan for a lab test — the ONLY call that marks the order
// "Returned to Lab" and emails the customer. Requires an admin/operator Bearer token.
export const markFormScanned = (labTestId) =>
  apiFetch(`/managePatientForms/form/${labTestId}/scan`, { method: "POST" });

// Fetch active lab tests for analysis with pagination
export const fetchActiveLabTests = (page = 1, limit = 130, filters = {}) =>
  apiFetch(`/manageProcessedOrders/activeLabTests?${buildListQueryParams(page, limit, filters)}`);

// Fetch review lab tests for admin review with pagination
export const fetchReviewLabTests = (page = 1, limit = 130) =>
  apiFetch(`/manageProcessedOrders/reviewLabTests?page=${page}&limit=${limit}`);

// Fetch completed lab tests with pagination
// Note: this endpoint only supports searchQuery + country (status is fixed to Completed)
export const fetchCompletedLabTests = (page = 1, limit = 130, filters = {}) =>
  apiFetch(
    `/manageCompletedOrders/completedLabTests?${buildListQueryParams(page, limit, {
      searchQuery: filters.searchQuery,
      country: filters.country,
    })}`
  );

// Fetch analysis details by order ID and test ID
export const fetchAnalysisDetails = async (orderId, testId) => {
  const responseData = await apiFetch(`/managePatientForms/getFormData/${orderId}/${testId}`);
  if (responseData.data && responseData.data.form_data) {
    return { ...responseData.data.form_data };
  }
  throw new Error("Invalid response structure from backend");
};

// Mark order with status
export const markOrder = (orderNumber, labTestId, status) =>
  apiFetch("/manageProcessedOrders/markOrder", {
    method: "POST",
    body: JSON.stringify({ order_number: orderNumber, lab_test_id: labTestId, status }),
  });

// Mark tests as processing when entry forms are downloaded
// Accepts array of items: [{ orderId, testId }, { orderId, testId }, ...]
export const markTestAsProcessing = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("Items must be a non-empty array of objects with orderId and testId");
  }
  return apiFetch("/manageTestAnalysis/markProcessing", {
    method: "POST",
    body: JSON.stringify(items),
  });
};

// Convenience function for marking a single test as processing
export const markSingleTestAsProcessing = (orderId, testId) =>
  markTestAsProcessing([{ orderId, testId }]);

// Fetch existing analysis data — returns null (not an error) the first time a test is analyzed
export const fetchAnalysisData = async (orderId, testId) => {
  try {
    return await apiFetch("/manageTestAnalysis/getAnalysis", {
      method: "POST",
      body: JSON.stringify({ order_id: orderId, test_id: testId }),
    });
  } catch (err) {
    if (err.status === 404 && err.message?.includes("Lab test not found")) {
      return null;
    }
    throw err;
  }
};

// Submit analysis data
export const submitAnalysisData = (orderId, testId, jsonData) =>
  apiFetch("/manageTestAnalysis/submitAnalysis", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId, test_id: testId, ...jsonData }),
  });

// OCR Scan - Send captured image and test ID
export const submitOCRScan = (testId, imageBase64) =>
  apiFetch("/manageOCRdata/scan", {
    method: "POST",
    body: JSON.stringify({ testId, image: imageBase64 }),
  });

// Send report via email using FormData with PDF blob
export const sendReport = (labTestId, dateOfBirth, pdfBlob) => {
  const formData = new FormData();
  formData.append("lab_test_id", labTestId);
  formData.append("dob", dateOfBirth);
  formData.append("pdf", pdfBlob, `report-${labTestId}.pdf`);
  return apiFetch("/manageCompletedOrders/sendReport", { method: "POST", body: formData });
};

// Mark order as completed
export const markOrderCompleted = (orderId, testId) =>
  apiFetch("/manageCompletedOrders/markCompleted", {
    method: "POST",
    body: JSON.stringify({ orderId, testId }),
  });

// Verify DOB using lab test ID for report access and download PDF — public
export const verifyDOB = async (dob, testId) => {
  const response = await fetch(`${API_BASE_URL}/manageCompletedOrders/verifyDOB`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "ngrok-skip-browser-warning": "true" },
    body: JSON.stringify({ dob, test_id: testId }),
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/pdf")) {
    throw new Error("Expected PDF response but got: " + contentType);
  }

  const pdfBlob = await response.blob();
  const url = window.URL.createObjectURL(pdfBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Report-${testId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { success: true, message: "PDF downloaded successfully" };
};

// Dashboard Analytics Functions

export const fetchMonthlyOrders = () => apiFetch("/manageDashboard/analytics/monthlyOrders");

export const fetchOrdersByCountry = () => apiFetch("/manageDashboard/analytics/ordersByCountry");

// Fetch order country by order ID
export const fetchOrderCountry = (orderId) =>
  apiFetch("/manageTestAnalysis/orderCountry", { method: "POST", body: JSON.stringify({ orderId }) });

// Fetch order language by order ID
export const fetchOrderLanguage = (orderId) =>
  apiFetch("/manageTestAnalysis/orderLanguage", { method: "POST", body: JSON.stringify({ orderId }) });

// Fetch order language by order ID — public (patient-facing), used to pick
// the submission form's language (admin-overridable via Edit Order)
export const fetchOrderLanguagePatient = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/manageTestAnalysis/orderLanguagePatient`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "true",
    },
    body: JSON.stringify({ orderId }),
  });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  return response.json();
};

// Fetch recent activities
// limit is capped by the backend at 50; offset paginates for infinite-scroll loading.
export const fetchRecentActivities = (limit = 30, offset = 0) =>
  apiFetch(`/manageActivities/activities/recent?limit=${limit}&offset=${offset}`);

// Fetch daily trends analytics
export const fetchDailyTrends = () => apiFetch("/manageDashboard/analytics/dailyTrends");

// Shared GET helper for the /manageDashboard/analytics/* endpoints below
const fetchDashboardAnalytics = (path, params = {}) => {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/manageDashboard/analytics/${path}${query ? `?${query}` : ""}`);
};

// Users (admin/operator/patient/therapist) summary
export const fetchUsersSummary = () => fetchDashboardAnalytics("usersSummary");

// Therapists summary (verified/unverified, top therapists by patient count)
export const fetchTherapistsSummary = () => fetchDashboardAnalytics("therapistsSummary");

// Patient/therapist signup trends by month
export const fetchRegistrationTrends = (months = 6) =>
  fetchDashboardAnalytics("registrationTrends", { months });

// SIBO form submission volume + consent/therapist-link rates by month
export const fetchFormSubmissions = (months = 6) =>
  fetchDashboardAnalytics("formSubmissions", { months });

// Average days order -> submission -> result
export const fetchLabTurnaround = () => fetchDashboardAnalytics("labTurnaround");

// All-time order_progress.status breakdown (all 8 real status values)
export const fetchOrderStatusBreakdown = () => fetchDashboardAnalytics("orderStatusBreakdown");

// Monthly revenue + order count
export const fetchRevenueTrends = (months = 12) => fetchDashboardAnalytics("revenueTrends", { months });

// Mark order as returned to lab
export const markOrderReturned = (orderId, testId) =>
  apiFetch("/orders/markReturned", { method: "POST", body: JSON.stringify({ orderId, testId }) });

// Update lab test substrate
export const updateLabTestSubstrate = (orderId, testIdentifier, substrate) =>
  apiFetch("/manageEditOrders/updateLabTestSubstrate", {
    method: "POST",
    body: JSON.stringify({ order_id: orderId, test_identifier: testIdentifier, substrate }),
  });

// Update order language by order number
export const updateOrderLanguage = (orderId, language) =>
  apiFetch("/manageEditOrders/updateOrderLanguage", {
    method: "POST",
    body: JSON.stringify({ orderId, language }),
  });

// Look up an order's current language + available options by its RAW
// INTERNAL orders.id (not order_number - see updateOrderLanguage above,
// which takes order_number instead).
export const fetchOrderLanguageById = (internalOrderId) =>
  apiFetch(`/manageEditOrders/orderLanguage?orderId=${internalOrderId}`);

// Fetch order substrates by order ID
export const fetchOrderSubstrates = (orderID) =>
  apiFetch(`/manageEditOrders/orderSubstrates?orderID=${orderID}`);

// Current user's own profile (any authenticated role)
export const fetchMyProfile = () => apiFetch("/profile/me");

// Update current user's own profile — name, countryId, languageId, and/or consent fields
export const updateMyProfile = (profileData) =>
  apiFetch("/profile/me", { method: "PUT", body: JSON.stringify(profileData) });

// Reference data for the country/language pickers (registration, profile, settings).
// Public endpoints — no auth required, so these also work pre-login.
export const fetchCountries = () => apiFetch("/countries");
export const fetchLanguages = () => apiFetch("/languages");

export const API_ENDPOINTS = {
  ORDERS_SUMMARY: (page = 1, limit = 130) =>
    `${API_BASE_URL}/orders/summary?page=${page}&limit=${limit}`,
  THERAPISTS_REGISTER: `${API_BASE_URL}/manageTherapists/registerTherapists`,
  THERAPISTS_DELETE: (id) =>
    `${API_BASE_URL}/manageTherapists/deleteTherapist/${id}`,
  THERAPISTS_UPDATE: (id) =>
    `${API_BASE_URL}/manageTherapists/updateTherapists/${id}`,
  THERAPISTS_LIST: (page = 1, limit = 130) =>
    `${API_BASE_URL}/manageTherapists/getTherapists?page=${page}&limit=${limit}`,
  THERAPISTS_LIST_CLIENT: (page = 1, limit = 130) =>
    `${API_BASE_URL}/manageTherapists/getTherapistsForClient?page=${page}&limit=${limit}`,
  THERAPISTS_ADD_FOR_CLIENT: `${API_BASE_URL}/manageTherapists/addTherapistsForClient`,
  FORMS_SUBMIT: `${API_BASE_URL}/managePatientForms/submitForm`,
  FORMS_GET_BY_LAB_TEST_ID: (labTestId) => `${API_BASE_URL}/managePatientForms/form/${labTestId}`,
  ACTIVE_LAB_TESTS: (page = 1, limit = 130) => `${API_BASE_URL}/manageProcessedOrders/activeLabTests?page=${page}&limit=${limit}`,
  REVIEW_LAB_TESTS: (page = 1, limit = 130) => `${API_BASE_URL}/manageProcessedOrders/reviewLabTests?page=${page}&limit=${limit}`,
  COMPLETED_LAB_TESTS: (page = 1, limit = 130) => `${API_BASE_URL}/manageCompletedOrders/completedLabTests?page=${page}&limit=${limit}`,
  ANALYSIS_DETAILS: (orderId, testId) => `${API_BASE_URL}/managePatientForms/getFormData/${orderId}/${testId}`,
  SCAN_BARCODE: (shortCode) => `${API_BASE_URL}/managePatientForms/scan/${shortCode}`,
  ANALYSIS_GET: `${API_BASE_URL}/manageTestAnalysis/getAnalysis`,
  ANALYSIS_SUBMIT: `${API_BASE_URL}/manageTestAnalysis/submitAnalysis`,
  OCR_SCAN: `${API_BASE_URL}/ocr/scan`,
  SEND_REPORT: `${API_BASE_URL}/manageCompletedOrders/sendReport`,
  MARK_COMPLETED: `${API_BASE_URL}/manageCompletedOrders/markCompleted`,
  VERIFY_DOB: `${API_BASE_URL}/manageCompletedOrders/verifyDOB`,
  // Dashboard Analytics Endpoints
  MONTHLY_ORDERS: `${API_BASE_URL}/manageDashboard/analytics/monthlyOrders`,
  ORDERS_BY_COUNTRY: `${API_BASE_URL}/manageDashboard/analytics/ordersByCountry`,
  ORDER_COUNTRY: `${API_BASE_URL}/orderCountry`,
  ORDER_LANGUAGE: `${API_BASE_URL}/manageTestAnalysis/orderLanguage`,
  DAILY_TRENDS: `${API_BASE_URL}/manageDashboard/analytics/dailyTrends`,
  RECENT_ACTIVITIES: (limit = 20) => `${API_BASE_URL}/manageActivities/activities/recent?limit=${limit}`,
  UPDATE_LAB_TEST_SUBSTRATE: `${API_BASE_URL}/manageEditOrders/updateLabTestSubstrate`,
  UPDATE_ORDER_LANGUAGE: `${API_BASE_URL}/manageEditOrders/updateOrderLanguage`,
  ORDER_LANGUAGE_BY_ID: (internalOrderId) => `${API_BASE_URL}/manageEditOrders/orderLanguage?orderId=${internalOrderId}`,
  ORDER_SUBSTRATES: (orderID) => `${API_BASE_URL}/manageEditOrders/orderSubstrates?orderID=${orderID}`,
};

// Export as named exports and default
const apiService = {
  fetchOrders,
  fetchOrderCountries,
  registerTherapist,
  deleteTherapist,
  updateTherapist,
  fetchTherapists,
  fetchTherapistForClient,
  addTherapistForClient,
  submitSiboForm,
  fetchFormByLabTestId,
  markFormScanned,
  fetchActiveLabTests,
  fetchReviewLabTests,
  fetchCompletedLabTests,
  fetchAnalysisDetails,
  markOrder,
  markTestAsProcessing,
  markSingleTestAsProcessing,
  fetchAnalysisData,
  submitAnalysisData,
  submitOCRScan,
  sendReport,
  markOrderCompleted,
  verifyDOB,
  markOrderReturned,
  updateLabTestSubstrate,
  updateOrderLanguage,
  fetchOrderLanguageById,
  fetchOrderSubstrates,
  fetchMyProfile,
  updateMyProfile,
  fetchCountries,
  fetchLanguages,
  // Patient/Therapist Portal Functions
  fetchPatientDashboard,
  fetchPatientOrders,
  fetchPatientReports,
  fetchPatientReportLink,
  fetchTherapistDashboard,
  fetchTherapistPatients,
  fetchTherapistPatientOrders,
  fetchTherapistReports,
  fetchTherapistReportLink,
  // Dashboard Analytics Functions
  fetchMonthlyOrders,
  fetchOrdersByCountry,
  fetchOrderCountry,
  fetchOrderLanguage,
  fetchOrderLanguagePatient,
  fetchRecentActivities,
  fetchDailyTrends,
  fetchUsersSummary,
  fetchTherapistsSummary,
  fetchRegistrationTrends,
  fetchFormSubmissions,
  fetchLabTurnaround,
  fetchOrderStatusBreakdown,
  fetchRevenueTrends,
  API_ENDPOINTS,
};

export default apiService;
